import Constants from "expo-constants";
import { Platform } from "react-native";

export type DeploymentEnvironment = "local" | "preview" | "production";

function resolveDeploymentEnvironment(): DeploymentEnvironment {
  if (Platform.OS === "web") {
    // The web build runs directly on its real hosting domains -- read the
    // environment off the actual browser location instead of threading a
    // build-time env var through Metro. (Constants.expoConfig isn't
    // reliably populated at runtime in a static web export, and Metro's
    // transform cache doesn't reliably bust on env var changes either --
    // both bit us before landing on this.) `window` is undefined during
    // the static-export prerender step (Node.js, not a browser); "local"
    // there is harmless since hydration re-evaluates this in the real
    // browser immediately after.
    if (typeof window === "undefined") return "local";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "local";
    // Cloudflare Workers: use.<domain> is production, dev-use.<domain> (and
    // any *.workers.dev URL) is preview.
    if (host.startsWith("dev-use.") || host.endsWith(".workers.dev"))
      return "preview";
    return "production";
  }
  // Native builds: `ENVIRONMENT` in .env (or the eas.json build profile) reaches
  // the app through app.config.ts. DEVELOPMENT is the local DDEV stack, TESTING
  // the Coolify preview API; anything else, unset included, is production.
  const requested = String(
    Constants.expoConfig?.extra?.environment ?? "",
  ).toUpperCase();
  return requested === "DEVELOPMENT"
    ? "local"
    : requested === "TESTING"
      ? "preview"
      : "production";
}

const deploymentEnvironment = resolveDeploymentEnvironment();

type EnvironmentConstants = {
  siteUrl: string;
  appUrl: string;
  apiUrl: string;
  sentryAppDsn?: string;
  turnstileSiteKey?: string;
};

const sentryAppDsn =
  "https://f87b569322a02da62992cc4b61aaab0d@o4504087623761920.ingest.us.sentry.io/4511914116055040";
const turnstileSiteKey = "0x4AAAAAAEsjH_cmkdXQF51V";

const constantsByEnvironment: Record<
  DeploymentEnvironment,
  EnvironmentConstants
> = {
  local: {
    siteUrl: "http://localhost:3000",
    appUrl: "http://localhost:8081",
    apiUrl: "https://mintstint-api.ddev.site",
  },
  preview: {
    siteUrl: "https://mintstint-website.pages.dev",
    appUrl: "https://dev-use.mintstint.app",
    apiUrl: "https://dev-api.mintstint.app",
    sentryAppDsn,
    turnstileSiteKey,
  },
  production: {
    siteUrl: "https://www.mintstint.app",
    appUrl: "https://use.mintstint.app",
    apiUrl: "https://api.mintstint.app",
    sentryAppDsn,
    turnstileSiteKey,
  },
};

const environmentConstants = constantsByEnvironment[deploymentEnvironment];

export const DEPLOYMENT_ENVIRONMENT = deploymentEnvironment;
export const SITE_URL = environmentConstants.siteUrl;
export const APP_URL = environmentConstants.appUrl;
export const SENTRY_APP_DSN = environmentConstants.sentryAppDsn;
export const TURNSTILE_SITE_KEY = environmentConstants.turnstileSiteKey;
// The API origin is public client configuration. Local development talks to the DDEV site
// (`ddev start` in mintstint-api); override with EXPO_PUBLIC_API_URL, e.g. for a phone on the LAN.
export const API_URL =
  process.env.EXPO_PUBLIC_API_URL?.trim() || environmentConstants.apiUrl;
export const backendConfigured = Boolean(API_URL);

export const SENTRY_ENABLED = Boolean(SENTRY_APP_DSN);
export const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);
export const REGISTRATION_ENABLED = true;

// RevenueCat SDK keys identify the public app; they are not server secrets.
// A Test Store key had been copied into all four slots, so a production build
// would have talked to the Test Store while looking configured. Only `test`
// keeps it; the platform keys must be the real appl_/goog_ keys before release.
// RevenueCat Web Billing (Stripe): live keys on production, sandbox keys (Stripe
// test cards) everywhere else.
const REVENUECAT_WEB_KEYS = {
  sandbox: "rcb_sb_fHvQTugpCWKXflGgLQpIJGQXx",
  production: "rcb_GigkyclEdgeqMjYduCjHGIYlUBLR",
} as const;

export const REVENUECAT_API_KEYS = {
  ios: "appl_htCtmPSdmNDpTHLltWmutceHCMr",
  android: "goog_wBkotvDEPKjymdvxvtVUHZgbRQN",
  // Test Store key: non-production native builds.
  test: "test_leWXwmUPUFPzThxMfhxjORUrOER",
  web:
    DEPLOYMENT_ENVIRONMENT === "production"
      ? REVENUECAT_WEB_KEYS.production
      : REVENUECAT_WEB_KEYS.sandbox,
} as const;

export const DEFAULT_REGIONAL_SETTINGS = {
  currency: "NZD",
  dateFormat: "en-NZ",
  timezone: "Pacific/Auckland",
} as const;

export const STORE_LINKS = {
  ios: "",
  android: "",
} as const;

export const EXTERNAL_LINKS = {
  privacyPolicy: `${SITE_URL}/legal/privacy-policy/`,
  terms: `${SITE_URL}/legal/terms-and-conditions/`,
  facebook: "",
  instagram: "",
  help: `${SITE_URL}/help/`,
} as const;
